"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CACHE_KEYS = void 0;
const COUNTRY_BASE = "-v2";
const REGION_BASE = "-v1";
const VEHICLE_MODELS_PREFIX = "retrieve-vehicle-models";
/**
 * @fileoverview Cache Key Constants
 *
 * @description
 * This module exports a centralized collection of cache keys used throughout the application.
 * Each key should be used to set cache only in one place.
 * Maintaining cache keys in a single location provides several important benefits:
 *
 * - Helps to prevent key collisions across different parts of the application
 * - Simplifies key management and updates
 * - Makes cache invalidation strategies more reliable
 * - Improves code readability with descriptive key naming
 * - Creates a single source of truth for all cache-related operations
 * - Makes it easier to track and monitor cache usage
 *
 * @example
 * // Handling breaking changes to cache structure
 * // When you need to modify the structure of cached data or invalidate all existing caches:
 *
 * // 1. Update the COUNTRY_BASE constant with a version prefix
 * // Before change:
 * // const COUNTRY_BASE = "";
 * // After change:
 * // const COUNTRY_BASE = "-v2";
 *
 * // This simple change invalidates all country-related caches by modifying the key pattern
 * // Example: 'retrieve-country-by-id' becomes 'retrieve-country-v2-by-id'
 *
 * // Benefits:
 * // - No need to manually flush all affected keys in Redis
 * // - Old cache entries naturally expire over time
 * // - No downtime required for cache migration
 * // - Different versions of the application can run simultaneously using different cache keys
 * // - Progressive rollout of changes becomes possible
 * // - Provides a way to roll back changes if issues are discovered
 *
 * @module CacheKeys
 */
exports.CACHE_KEYS = {
    COUNTRIES: {
        COUNTRY_BY_ID: `country${COUNTRY_BASE}-by-id`,
        CONTENT_COUNTRY_BY_ID: `content:country${COUNTRY_BASE}-by-id`,
        COUNTRY_BY_ISO: `country${COUNTRY_BASE}-by-iso`,
        CONTENT_COUNTRY_BY_ISO: `content:country${COUNTRY_BASE}-by-iso`,
        COUNTRIES_BY_ISO: `countries${COUNTRY_BASE}-by-iso-codes`,
        COUNTRY_BY_LOCATION_ID: `country${COUNTRY_BASE}-by-location-id`,
        COUNTRY_BY_COORDINATES: `country${COUNTRY_BASE}-by-coordinates`,
        COUNTRY_ROUTES: `country${COUNTRY_BASE}-routes`,
        COUNTRIES_COUNT_PUBLIC: `countries${COUNTRY_BASE}-count-public`,
        COUNTRIES_WITH_ROUTES: `countries${COUNTRY_BASE}-with-active-routes-with-continent-field`,
        ALL: `all-countries${COUNTRY_BASE}`,
        ALL_BY_IDS: `countries${COUNTRY_BASE}`,
        PRICING_FOR_PRICING_AREA: `pricing-for-countries${COUNTRY_BASE}-and-regions${REGION_BASE}`,
    },
    REGIONS: {
        ALL: `get-all-regions${REGION_BASE}`,
    },
    REVIEWS: {
        REVIEWS_COUNT_PUBLIC: "reviews-count-public",
        ROUTE_REVIEWS_COUNT_PUBLIC: "retrieve-route-reviews-count-public",
        LATEST_REVIEW: "retrieve-latest-review",
        ROUTE_REVIEWS: "retrieve-route-reviews",
        DAY_TRIP_REVIEWS: "retrieve-day-trip-reviews",
    },
    LOCATIONS: {
        LOCATION: "get-location-v1",
        LOCATIONS: "get-locations-v1",
        LOCATIONS_COUNT_PUBLIC: "locations-count-public",
        SIMPLE_LOCATIONS: "retrieve-simple-locations",
        SAMPLE_LOCATIONS: "get-sample-locations",
        LOCATIONS_BY_MACHINE_NAME: "get-locations-by-machine-names-v1",
        NEAREST_LOCATION_TO_COORDINATES: "get-nearest-location-to-coordinates",
        WORLDWIDE_POPULAR_LOCATIONS: "getWorldwidePopularLocations",
        POPULAR_TRANSFER_CITIES_BY_COUNTRY: "get-popular-transfer-cities-by-country",
        NO_ORIGIN_OR_DESTINATION: "noOriginOrDestinationQuery",
        ORIGIN_OR_DESTINATION: "originOrDestinationQuery",
        ROUTE_SELECTOR_LOCATIONS_BY_SEARCH_STRING: "getRouteSelectorLocationsBySearchStringV2",
        SELECTED_LOCATION_DATA: "route-selector_getSelectedLocationData",
        GOOGLE_LOCATION_FROM_ID: "route-selector_getGoogleLocationFromId",
    },
    ORDERS: {
        ORDERS_COUNT_PUBLIC: "orders-count-public",
        ORDER_INSURANCE_LOG: "order-insurance-log",
        OFFER_PRICE_ADJUSTMENTS: "offer:pricing-adjustments",
    },
    COMBOS: {
        ORDERS_TRAVEL_DATA: "ComboOrdersTravelData",
        ORDERS_LOCATION_DATA: "ComboOrdersLocationData2",
        COMBOS: "retrieve-combos",
        IS_RECALCULATING_COMBOS: "is-recalculating-combos",
        EXPIRES_SOON: "comboExpiresSoon",
    },
    ROUTES: {
        ROUTE_CUSTOMERS_COUNT_PUBLIC: "route-customers-count-public",
        ROUTES_COUNT_PUBLIC: "routes-count-public",
        ROUTE_PRICING_CURRENCY: "retrieve-route-pricing-currency",
        ROUTES_BY_POPULARITY: "get-routes-by-popularity",
        ROUTES_FROM_LOCATION: "retrieve-routes-from-location",
        ROUTES_TO_LOCATION: "retrieve-routes-to-location",
        BY_ID: "get-route",
        COMPUTE_ROUTES: "routes-client_computeRoutes",
        TOLL_FEE: "routes-toll-fee",
        ROUTE_BY_MACHINE_NAME: "retrieve-route-by-machine-name-v1",
        ROUTE_BY_ORIGIN_DESTINATION_MACHINE_NAMES: "retrieve-route-by-origin-destination-machine-names-v1",
    },
    DAYTRIPS: {
        BY_POPULARITY: "day-trips-by-popularity",
        EXPLORE_POPULAR_DAY_TRIPS: "day-trips-explore-popular",
        EXPLORE_MORE_DAY_TRIPS: "day-trips-explore-more",
        OPTIONS_BY_POPULARITY: "day-trips-popular-options-v2",
        DAY_TRIPS_ORIGINS: "day-trips-origins",
    },
    FAQ: {
        FREQUENTLY_ASKED_QUESTIONS: "retrieve-frequently-asked-questions",
        DAY_TRIPS_FREQUENTLY_ASKED_QUESTIONS: "retrieve-day-trips-frequently-asked-questions",
        FAMILY_FREQUENTLY_ASKED_QUESTIONS: "retrieve-family-frequently-asked-questions",
        SOLO_FEMALE_FREQUENTLY_ASKED_QUESTIONS: "retrieve-solo-female-frequently-asked-questions",
    },
    TRANSLATIONS: {
        TRANSLATIONS_BY_LANGUAGE: "get-translations-by-langugage",
    },
    VEHICLES: {
        MODEL_IDS_BY_TRANSPORTER_ID: `vehicle-model-ids-by-transporter-id`,
    },
    VEHICLE_MODELS: {
        PREFIX: VEHICLE_MODELS_PREFIX,
        ALL: `${VEHICLE_MODELS_PREFIX}-all`,
        BY_ID: `${VEHICLE_MODELS_PREFIX}-by-id`,
        ALL_BY_ID: `${VEHICLE_MODELS_PREFIX}-all-by-id`,
    },
    DRIVERS: {
        DRIVERS_COUNT_PUBLIC: "drivers-count-public",
        APPROVED_DAYTRIP_DRIVERS: "fo:driver_availability:approved_daytrip_drivers",
        APPROVED_COMPANY_DRIVERS: "fo:driver_availability:approved_daytrip_company_drivers",
        APPROVED_VEHICLES: "fo:driver_availability:approved_vehicles",
        COMPANIES_WILLING_TO_RENT: "fo:driver_availability:drivers_companies_willing_to_rent_vehicles",
        TRANSPORTATION_PROVIDERS: "fo:DriverAvailabilityProvider:transportationProvidersCache",
        ALLOWED_DRIVER_COUNTRY_RULES: "allowed-driver-country-rules",
        AUTO_ACCEPTED_DRIVER_COUNTRY_RULES: "auto-accepted-driver-country-rules",
    },
    GEO: {
        EVALUATE_TRAVEL_DATA: "evaluate-travel-data",
        ADDRESS_INFO: "retrieve-address-info",
        POSITION_BY_ADDRESS: "maps-service_fetchPositionByAddress",
        GEO_DATA: "fo:gm",
        GEOCODING_RESULTS: "geo-data-provider_geocodingResults",
        LOCATION_BY_PLACE_ID: "geo-data-provider_getLocationByPlaceId",
        LAND_MASSES: "geo:land-masses-v2",
        GOOGLE_LOCATIONS: "route-selector_getGoogleLocations",
        ORIGIN_DESTINATION_TRAVEL_DATA: "travel_data",
        ROUTE_SELECTOR_LOCATIONS_BY_GEOPOINT: "route-selector_getRouteSelectorLocationsByGeoPoint",
    },
    PAYMENTS: {
        MANGOPAY_RATE_LIMIT: "mangopay_rate_limits",
        FRESH_MANGOPAY_RATE_LIMIT: "fresh:mangopay:rate-limits",
    },
    ADS: {
        ADWORDS_FEED: "get-adwords-feed-csv-v2",
        META_FEED: "get-meta-feed-csv",
        REMARKETING_FEED: "get-remarketing-feed-csv",
    },
    ASSIGNATIONS: {
        OFFER_POSTING_INFO_KEY_PREFIX: "cached-vehicle-offer-posting-info",
        PAPI_ASSIGNED_DRIVERS: "papi-get-assigned-drivers",
    },
    ASSIGNATION_OFFERS: {
        AUTO_POST_ASSIGNATION_OFFER_FOR_VEHICLE: "auto-post-assignation-offer-for-vehicle",
    },
    CURRENCY: {
        CONVERSION_RATE: "partner_api:get_conversion_rate",
    },
    MEETING_POSITIONS: {
        BY_ID: "get-meeting-position",
        BY_LOCATION_ID: "get-meeting-positions-for-location",
        FOR_RESTRICTED_AREA: "get-meeting-positions-for-restricted-area",
    },
    TRIPS: {
        IF_TRIP_QUALIFIES: "fo:availability:trip_qaulifies",
    },
    CREDIT_LINE_CONVERSION_RATE: "credit_line:latest_conversion_rate",
    MAPS: {
        APPLE_MAPS_REDUCED_ROUTE_POINTS: "apple-maps-reduced-route-points",
    },
    HOST_AGENCY: {
        STATISTICS: "host-agency-statistics",
        AGENT_OF_THE_MONTH: "host-agency-agent-of-the-month",
    },
};
//# sourceMappingURL=cache-keys.js.map